import { Grid, Link, Tooltip } from "@mui/material";
import { GridActionsCellItem, GridColDef, GridColumnVisibilityModel } from "@mui/x-data-grid-pro";
import { Ico_BrokenLink } from "@sagecentral/core-ui";
import { IDashboardSettings } from "components/preferences";
import { TaskListTooltip } from "components/shared";
import { NumberFilterOperator, StatusFilterOperator, TextFilterOperator } from "components/shared/filters";
import ContainsOnlyTextFilterOperator from "components/shared/filters/ContainsOnlyTextFilterOperator";
import { TaskStatusCell } from "components/tasks";
import ParentWorkItemListTooltip from "components/teams/grid/ParentWorkItemListTooltip";
import { SetPreferenceSignature } from "hooks/preferences";
import useGridColumns, { ColumnVisibilityOrOrderChangeHandler } from "hooks/shared/useGridColumns";
import { UnrelateWorkItemSignature, useWorkItemPermissions } from "hooks/work-items";
import { IListItem } from "models/common";
import { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { toPascalCase } from "utils/string-utils";

type UseRelatedWorkItemsColumnsResult = [
    GridColDef[],
    GridColumnVisibilityModel,
    ColumnVisibilityOrOrderChangeHandler
];

type UseRelatedWorkItemsColumnsProps = {
    unrelate: UnrelateWorkItemSignature;
    settings?: IDashboardSettings | null;
    setSettings?: SetPreferenceSignature;
};

type UseRelatedWorkItemsColumnsSignatre = (props: UseRelatedWorkItemsColumnsProps) => UseRelatedWorkItemsColumnsResult;

const useRelatedWorkItemsColumns: UseRelatedWorkItemsColumnsSignatre = (props) => {
    const {
        unrelate,
        settings,
        setSettings
    } = props;
    const permissions = useWorkItemPermissions();

    const columnBase: GridColDef[] = [
        {
            field: 'workspace',
            headerName: 'Workspace',
            headerClassName: 'header',
            minWidth: 120, width: 200,
            sortable: true,
            resizable: true, hideable: false,
            hideSortIcons: false, disableReorder: true,
            filterOperators: TextFilterOperator,
            renderCell: params => {
                const name = params.value;
                const id = params.row.workspaceId;
                const url = process.env.REACT_APP_CLASSIC_URL + `/WorkspaceEnduser/View?WSId=${id}`;
                return (
                    <Tooltip title={name} arrow>
                        <Grid container alignItems="center"
                            style={{
                                whiteSpace: 'break-spaces',
                                lineHeight: '1.5em',
                                height: '3em'
                            }}
                        >
                            <Grid item xs={10}>
                                <Link href={url}>
                                    {name}
                                </Link>
                            </Grid>
                        </Grid>
                    </Tooltip>
                );
            }
        },
        {
            field: 'workItemId',
            headerName: 'WI ID',
            headerClassName: 'header',
            minWidth: 120, width: 200,
            sortable: true, resizable: true,
            hideable: false,
            hideSortIcons: false, disableReorder: true,
            filterOperators: NumberFilterOperator,
            renderHeader: (params) => {
                return (
                    <Tooltip title="Work Item ID" arrow>
                        <div>{"WI ID"}</div>
                    </Tooltip>
                )
            }
        },
        {
            field: 'workItem',
            headerName: 'Work Item',
            flex: 1,
            minWidth: 300, 
            sortable: true,
            resizable: true, hideable: false,
            headerClassName: 'header', hideSortIcons: false, disableReorder: true,
            filterOperators: TextFilterOperator,
            renderCell: params => {
                const name = params.value;
                const id = params.row.workItemId;
                const url = `/workitems/${id}`
                return (
                    <Tooltip title={name} arrow>
                        <Grid container alignItems="center"
                            style={{
                                whiteSpace: 'break-spaces',
                                lineHeight: '1.5em',
                                height: '3em'
                            }}
                        >
                            <Grid item xs={10}>
                                <Link to={url} component={RouterLink}>
                                    {name}
                                </Link>
                            </Grid>
                        </Grid>
                    </Tooltip>
                );
            }
        },
        {
            field: 'relationship',
            headerName: 'Relationship',
            headerClassName: 'header',
            minWidth: 120, width: 200,
            sortable: true, resizable: true,
            hideable: false,
            hideSortIcons: false,
            filterOperators: TextFilterOperator
        },
        {
            field: 'parentWorkItems', headerName: 'Parent Work Item', 
            minWidth: 120, width: 200, 
            sortable: true, resizable: true, hideable: true, 
            headerClassName: 'header', hideSortIcons: false,
            filterOperators: ContainsOnlyTextFilterOperator,
            renderCell: (params) => {
              const parents = params.value;
              return (
                <Grid container alignItems="center" sx={{
                    overflow: 'hidden',
                    whiteSpace: 'break-spaces', 
                    lineHeight: '1.5em', 
                    height: '3em'}}
                >
                    <Grid item xs={10}>
                      {parents?.length > 0 && <ParentWorkItemListTooltip 
                        canAccessAllWorkItems={permissions?.canAccessAllWorkItems ?? false} 
                        parent={parents ? parents : ''} 
                        wave4/>}
                    </Grid>
                  </Grid>
      
              );
            }
          },
        {
            field: 'inProgressTask',
            headerName: 'In Progress Task',
            minWidth: 120, width: 200,
            sortable: true,
            resizable: true, hideable: false,
            headerClassName: 'header', hideSortIcons: false, disableReorder: true,
            filterOperators: TextFilterOperator,
            renderCell: params => {                
                const list = params.value as IListItem[];
                return (
                    <Grid container alignItems="center" sx={{
                        overflow: 'hidden',
                        whiteSpace: 'break-spaces', 
                        lineHeight: '1.5em', 
                        height: '3em'}}
                    >
                        <Grid item xs={10}>
                          {list?.length > 0 && <TaskListTooltip showHyperlink={permissions?.canViewAllTasks} list={list}/>}
                        </Grid>
                      </Grid>
          
                  );
            }
        },
        { 
            field: 'status', 
            headerName: 'WI Status', 
            minWidth: 120, width: 200, 
            sortable: true, resizable: true, hideable: true, hideSortIcons: false,
            headerClassName: 'header', 
            filterOperators: StatusFilterOperator,
            renderHeader: (params) => {
                return (
                    <Tooltip title="Work Item Status" arrow>
                        <div>{params.colDef.headerName}</div>
                    </Tooltip>
                )
            },
            renderCell: (params) => {
                return (<TaskStatusCell status={params.value} />)
            }
        },
        {
            field: 'contentType',
            headerName: 'Content Type',
            headerClassName: 'header',
            minWidth: 120, width: 200,
            sortable: true, resizable: true,
            hideable: true,
            hideSortIcons: false,
            filterOperators: TextFilterOperator
        },
        {
            field: "unrelate",
            headerName: "Unrelate",
            type: 'actions',
            width: 100,
            hideable: false,
            getActions: (params) => {
                return [
                    <GridActionsCellItem
                        label='Unrelate'
                        onClick={() => unrelate(params.row.id)}
                        icon={<Ico_BrokenLink color='primary'/>}
                    />
                ];
            }
        }
    ];

    const columns = useMemo(() => {
        let newColumns = [];
        newColumns.push(...columnBase);
        return newColumns;
    }, [ ]);

    const [ 
        columnsModel, 
        visibilityModel, 
        handleColumnVisibilityOrOrderChange 
    ] = useGridColumns({ columns: columns, settings, setSettings })

    return [
        columnsModel,
        visibilityModel,
        handleColumnVisibilityOrOrderChange
    ];
};

export default useRelatedWorkItemsColumns;